import { Button, Flex, Group, IconFont, Input, Modal, Select, Text, Toast, Tooltip } from '@byecode/ui'
import { type DataSourceAbstract, DataSourceType } from '@lighthouse/core'
import type {
    ClickTriggerConfig,
    CreateRecordTriggerConfig,
    MatchConditionTriggerConfig,
    NodeTypes,
    ScheduledTriggerConfig,
    UpdateRecordTriggerConfig,
    WebhookTriggerConfig,
    WebsocketTriggerConfig
} from '@lighthouse/shared'
import {
    getIsIncludesDisableDataSourceInWorkFlow,
    getTableIcon,
    ListItem4ByecodeUi,
    SelectDataSource,
    TriggerNodeType,
    triggerNodeTypeCustomOptions,
    useAtomAction,
    WorkflowType
} from '@lighthouse/shared'
import copyToClipboard from 'copy-to-clipboard'
import React, { useCallback, useMemo } from 'react'

import { getDataSourceMetaAtom } from '@/atoms/dataSource/action'
import { promptSwitchDataSource } from '@/containers/FlowDetail/utils/promptSwitchDataSource'
import { buildWebhookUrl } from '@/containers/FlowDetail/utils/webhookUrl'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useWorkSpace } from '@/hooks/useWorkSpace'

import * as SC from './styles'

const ACTION_FLOW_TRIGGER_OPTIONS = [
    {
        label: '动作触发',
        value: TriggerNodeType.CLICK_TRIGGER
    },
    {
        label: 'Websocket触发',
        value: TriggerNodeType.WEBSOCKET_TRIGGER
    }
]

const HAS_DS_NODE_TYPES = new Set<TriggerNodeType>([
    TriggerNodeType.CREATE_RECORD_TRIGGER,
    TriggerNodeType.MATCH_CONDITION_TRIGGER,
    TriggerNodeType.UPDATE_RECORD_TRIGGER
])

export interface TriggerModeDataSourceValue {
    dataSourceId?: string
    url?: string
    eventName?: string
    eventValue?: string
}

interface TriggerModeDataSourceProps {
    nodeType: NodeTypes
    onNodeTypeChange: (val: NodeTypes) => void
    onNodeNameChange: (name: string) => void
    value?:
        | CreateRecordTriggerConfig
        | UpdateRecordTriggerConfig
        | MatchConditionTriggerConfig
        | ScheduledTriggerConfig
        | ClickTriggerConfig
        | WebsocketTriggerConfig
        | WebhookTriggerConfig
    onChange?: (
        value:
            | CreateRecordTriggerConfig
            | UpdateRecordTriggerConfig
            | MatchConditionTriggerConfig
            | ScheduledTriggerConfig
            | ClickTriggerConfig
            | WebsocketTriggerConfig
            | WebhookTriggerConfig
    ) => void
}

export const TriggerModeDataSource: React.FC<TriggerModeDataSourceProps> = ({
    nodeType,
    onNodeTypeChange,
    onNodeNameChange,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { apiKey } = useWorkSpace()

    const isWsTrigger = nodeType === TriggerNodeType.WEBSOCKET_TRIGGER
    const isWebhookTrigger = nodeType === TriggerNodeType.WEBHOOK_TRIGGER
    const showDsSelector = HAS_DS_NODE_TYPES.has(nodeType as TriggerNodeType)
    const { type, elRef } = useFlowContainerContext()

    const isFlowType = type === WorkflowType.ACTION

    const listDs = useDataSourceList(appId, envId)
    const { run: getDataSourceMeta } = useAtomAction(getDataSourceMetaAtom)
    const options = useMemo(
        () =>
            listDs.reduce<DataSourceAbstract[]>((listDs, item) => {
                const tableIcon = getTableIcon(item)
                if (!getIsIncludesDisableDataSourceInWorkFlow(item) && !item.sync && item.type !== DataSourceType.joinDataSource) {
                    listDs.push(item)
                }
                return listDs
            }, []),
        [listDs]
    )

    const handleGenerateWebhookUrl = useCallback(() => {
        const token = apiKey?.trim()
        if (!token) {
            Toast.error('请先在空间设置中生成 API Token')
            return
        }
        onChange?.({ ...(value as WebhookTriggerConfig), url: buildWebhookUrl(token) })
    }, [apiKey, onChange, value])

    return (
        <Group label="触发">
            <ListItem4ByecodeUi>
                <SC.FormItemLabelWrapper>
                    <SC.FormItemLabel>触发方式</SC.FormItemLabel>
                </SC.FormItemLabelWrapper>
                <SC.FormItemContent>
                    <Select
                        value={nodeType}
                        options={isFlowType ? ACTION_FLOW_TRIGGER_OPTIONS : triggerNodeTypeCustomOptions}
                        onChange={async val => {
                            if (nodeType === TriggerNodeType.CLICK_TRIGGER && val !== TriggerNodeType.CLICK_TRIGGER) {
                                const isConfirm = await Modal.confirm({
                                    title: '确定更改触发方式？',
                                    content: '更改后，应用内通过动作触发该流程的功能将会失效。',
                                    okText: '确定更改',
                                    okStatus: 'error'
                                })
                                if (!isConfirm) {
                                    return
                                }
                            }
                            const nextName = isFlowType
                                ? ACTION_FLOW_TRIGGER_OPTIONS.find(item => item.value === val)?.label
                                : triggerNodeTypeCustomOptions.find(item => item.value === val)?.label
                            if (nextName) {
                                onNodeNameChange(nextName)
                            }
                            onNodeTypeChange(val as NodeTypes)
                        }}
                    />
                </SC.FormItemContent>
            </ListItem4ByecodeUi>

            {showDsSelector && (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>数据表</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SelectDataSource
                            value={(value as CreateRecordTriggerConfig)?.dataSourceId}
                            searchable
                            withinPortal
                            placeholder="请选择数据表"
                            onChange={async dsId => {
                                const isConfirmed =
                                    (value as CreateRecordTriggerConfig)?.dataSourceId === undefined
                                        ? true
                                        : await promptSwitchDataSource(elRef?.current)
                                if (isConfirmed) {
                                    onChange?.({ ...(value as CreateRecordTriggerConfig), dataSourceId: dsId })
                                    getDataSourceMeta({ envId, dsId })
                                }
                            }}
                            dataSourceList={options}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )}

            {isWsTrigger && (
                <>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>Websocket URL</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Input
                                placeholder="ws(s)://"
                                value={(value as WebsocketTriggerConfig)?.url}
                                onChange={ev => onChange?.({ ...(value as WebsocketTriggerConfig), url: ev.target.value })}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>事件参数名</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Input
                                value={(value as WebsocketTriggerConfig)?.eventName}
                                onChange={ev => onChange?.({ ...(value as WebsocketTriggerConfig), eventName: ev.target.value })}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>事件参数值</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Input
                                value={(value as WebsocketTriggerConfig)?.eventValue}
                                onChange={ev => onChange?.({ ...(value as WebsocketTriggerConfig), eventValue: ev.target.value })}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )}

            {isWebhookTrigger && (
                <>
                    <ListItem4ByecodeUi justifyContent="space-between">
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>生成 Webhook URL</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Button block onClick={handleGenerateWebhookUrl}>
                                生成Webhook地址
                            </Button>
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>Webhook URL</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <Flex alignItems="center" gap={8} style={{ maxWidth: '180px' }}>
                            {(value as WebhookTriggerConfig)?.url ? (
                                <>
                                    <Tooltip title={(value as WebhookTriggerConfig)?.url}>
                                        <Text>{(value as WebhookTriggerConfig)?.url}</Text>
                                    </Tooltip>
                                    <Button
                                        size="sm"
                                        icon={<IconFont type="Copy" color="var(--color-gray-400)" />}
                                        onClick={() => {
                                            if (!(value as WebhookTriggerConfig)?.url) {
                                                return
                                            }
                                            const isCopied = copyToClipboard((value as WebhookTriggerConfig)?.url)
                                            if (isCopied) {
                                                Toast.success('复制成功')
                                            }
                                        }}
                                    />
                                </>
                            ) : null}
                        </Flex>
                    </ListItem4ByecodeUi>
                </>
            )}
        </Group>
    )
}
